import { useCallback, useEffect, useState } from 'react'
import { BrowserRouter } from 'react-router-dom'

import { onExpired, ServiceError } from './api'
import * as auth from './auth'
import type { Session, Whoami } from './auth'
import { LocaleProvider, useT } from './i18n/LocaleProvider'
import { LegacyInspectionRedirect } from './LegacyInspectionRedirect'
import { AppRouter, routePolicies } from './router'

export function App(): JSX.Element {
  if (routePolicies.length === 0) throw new Error('manca il registro delle route')

  const [who, setWho] = useState<Whoami | null>(null)
  const [inside, setInside] = useState<boolean | null>(null)
  const [unreachable, setUnreachable] = useState(false)

  const identify = useCallback(() => {
    auth
      .whoami()
      .then((found) => {
        setWho({ ...found, must_change_password: auth.needsPasswordChange() })
        setInside(true)
        setUnreachable(false)
      })
      .catch((error: unknown) => {
        /*
          «Il servizio ha rifiutato» e «il servizio non risponde» sono due
          cose opposte, e trattarle allo stesso modo buttava fuori l'operatore
          al momento peggiore: un F5 durante un'interruzione di rete cancellava
          il gettone e mostrava l'ingresso — trovato dal vivo (GI-203). Il
          rifiuto vero arriva come ServiceError; un guasto di rete arriva come
          eccezione del navigatore, e li' il gettone si tiene e si riprova da
          soli, perche' la rete torna e l'operatore non deve fare niente.
        */
        if (error instanceof ServiceError) {
          auth.forget()
          setInside(false)
          return
        }
        setUnreachable(true)
        window.setTimeout(identify, 5000)
      })
  }, [])

  useEffect(() => {
    onExpired(() => {
      auth.forget()
      setWho(null)
      setInside(false)
    })
    if (auth.resume() === null) setInside(false)
    else identify()
    return () => onExpired(null)
  }, [identify])

  const leave = (): void => {
    void auth.leave().finally(() => {
      setWho(null)
      setInside(false)
    })
  }

  if (unreachable && inside === null) {
    return (
      <LocaleProvider>
        <ServiceUnreachable />
      </LocaleProvider>
    )
  }

  return (
    <LocaleProvider>
      <BrowserRouter>
        <LegacyInspectionRedirect />
        <AppRouter
          inside={inside}
          who={who}
          onLeave={leave}
          onEntered={(session: Session) => {
            setInside(null)
            if (!session.must_change_password) {
              setWho(null)
            }
            identify()
          }}
        />
      </BrowserRouter>
    </LocaleProvider>
  )
}

/**
 * Il servizio non risponde, e la pagina lo dice invece di fingere un'uscita.
 *
 * Niente bottone «riprova»: la pagina riprova da sola ogni cinque secondi, e
 * un bottone insegnerebbe soltanto a martellarlo. Cio' che conta dire e' che
 * il lavoro non e' perso: gettone e casella d'uscita restano dove sono.
 */
function ServiceUnreachable(): JSX.Element {
  const t = useT()
  return (
    <main className="servizio-muto">
      <h1>{t('unreachable.title')}</h1>
      <p role="status">{t('unreachable.body')}</p>
    </main>
  )
}
